import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatDateTime } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { PhoneLink } from '@/components/PhoneLink'
import {
  formatEstimateSms,
  type EstimateForFormat,
} from '@/lib/estimate-format'

type PartItem = {
  description?: string | null
  vendor?: string | null
  qty?: number | null
  price_each?: number | null
  brand?: string | null
  part_number?: string | null
  [key: string]: unknown
}

type EstimateDetailRow = EstimateForFormat & {
  id: string
  status: string
  customer_id: string | null
  vehicle_id: string | null
  created_at: string
  customer_phone: string | null
  customer_email: string | null
  parts: { source?: string | null; primary?: PartItem[] | null } | null
}

export function EstimateDetail() {
  const { t } = useTranslation('estimates')
  const { t: tCommon } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const { getToken, isLoaded } = useAuth()
  const [smsOpen, setSmsOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const estimateQuery = useQuery({
    queryKey: ['estimate', id],
    enabled: isLoaded && !!id,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/estimates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as EstimateDetailRow
    },
  })

  const smsText = useMemo(() => {
    if (!estimateQuery.data) return ''
    return formatEstimateSms(estimateQuery.data, { shopName: 'GYM Auto' })
  }, [estimateQuery.data])

  async function handleCopy() {
    if (!smsText) return
    try {
      await navigator.clipboard.writeText(smsText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback path: select the textarea so the mechanic can copy manually
      // — clipboard API is blocked in some embedded webview contexts.
      const ta = document.getElementById(
        'sms-textarea',
      ) as HTMLTextAreaElement | null
      ta?.select()
    }
  }

  if (estimateQuery.isLoading || !estimateQuery.data) {
    return (
      <div className="mx-auto w-full max-w-[660px] space-y-4 lg:max-w-[720px]">
        <Link
          to="/estimates"
          className="-ml-2 inline-flex min-h-11 items-center px-2 text-sm text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('detail.back')}
        </Link>
        {estimateQuery.isError ? (
          <div className="rounded-[14px] border border-destructive/25 bg-destructive-soft p-4 text-sm text-destructive">
            {estimateQuery.error instanceof Error
              ? estimateQuery.error.message
              : t('detail.error')}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {tCommon('loading')}
          </div>
        )}
      </div>
    )
  }

  const e = estimateQuery.data
  const vehicle = [e.vehicle_year, e.vehicle_make, e.vehicle_model]
    .filter(Boolean)
    .join(' ')
  const partsList = e.parts?.primary ?? []

  return (
    <div className="mx-auto w-full max-w-[660px] space-y-6 lg:max-w-[720px]">
      {/* §4.5 detail header — 44px back-button row above the 22px title,
          single primary action in the right slot. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/estimates"
            className="-ml-2 inline-flex min-h-11 items-center px-2 text-sm text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
          >
            {t('detail.back')}
          </Link>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('detail.title', { id: e.id })}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('detail.subtitle', {
              status: e.status,
              created: formatDateTime(e.created_at),
            })}
          </p>
        </div>
        <Button type="button" size="pill" onClick={() => setSmsOpen(true)}>
          <Send className="size-4" />
          {t('detail.send_to_customer')}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="space-y-1 rounded-[14px] border border-border bg-card p-4">
          <div className="text-[13px] font-semibold text-muted-foreground">
            {t('detail.customer_header')}
          </div>
          <div className="text-base font-semibold text-foreground">
            {e.customer_name ?? '—'}
          </div>
          <div>
            <PhoneLink
              phone={e.customer_phone}
              className="font-mono text-xs tabular-nums text-muted-foreground"
            />
          </div>
          {e.customer_email ? (
            <div className="text-xs text-muted-foreground">{e.customer_email}</div>
          ) : null}
        </section>

        <section className="space-y-1 rounded-[14px] border border-border bg-card p-4">
          <div className="text-[13px] font-semibold text-muted-foreground">
            {t('detail.vehicle_header')}
          </div>
          <div className="text-base font-semibold text-foreground">
            {vehicle || '—'}
          </div>
          {e.vehicle_engine ? (
            <div className="text-xs text-muted-foreground">{e.vehicle_engine}</div>
          ) : null}
          {e.vehicle_vin ? (
            <div className="font-mono text-xs tabular-nums text-muted-foreground">
              VIN {e.vehicle_vin}
            </div>
          ) : null}
        </section>
      </div>

      <section className="rounded-[14px] border border-border bg-card p-4">
        <div className="mb-2 text-[13px] font-semibold text-muted-foreground">
          {t('detail.work_header')}
        </div>
        <div className="text-sm text-foreground">
          {e.work_description ?? '—'}
        </div>
      </section>

      <section className="rounded-[14px] border border-border bg-card p-4">
        <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
          {t('detail.parts_header')}
        </div>
        {partsList.length === 0 ? (
          <div className="rounded-[10px] border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
            {t('detail.parts_empty')}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {partsList.map((p, i) => {
              const qty = p.qty ?? 1
              const each = p.price_each ?? 0
              const lineTotal = qty * each
              return (
                <li
                  key={i}
                  className="flex items-start justify-between gap-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">
                      {p.description ??
                        (typeof p.part_number === 'string'
                          ? p.part_number
                          : null) ??
                        '—'}
                    </div>
                    {p.vendor ? (
                      <div className="text-xs text-muted-foreground">{p.vendor}</div>
                    ) : null}
                  </div>
                  <div className="whitespace-nowrap text-right">
                    <div className="font-mono text-[12px] tabular-nums text-ink-3">
                      {qty} × {formatMoney(each)}
                    </div>
                    <div className="font-mono font-semibold tabular-nums text-foreground">
                      {formatMoney(lineTotal)}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* Neutral surface — quote totals are not a system save (§2). */}
      <section className="rounded-[14px] border border-border bg-muted p-4">
        <div className="space-y-1.5 text-sm">
          <Row label={t('detail.totals.parts')} value={e.parts_subtotal} />
          <Row label={t('detail.totals.labor')} value={e.labor_subtotal} />
          <Row label={t('detail.totals.subtotal')} value={e.subtotal} />
          <Row label={t('detail.totals.tax')} value={e.tax} />
          <div className="flex justify-between border-t border-input pt-1.5 text-base font-bold text-foreground">
            <span>{t('detail.totals.total')}</span>
            <span className="font-mono tabular-nums">{formatMoney(e.total)}</span>
          </div>
        </div>
      </section>

      {/* §4.7 confirm-or-refuse surface — a customer-facing send. Paper
          card, line-strong frame (NOT green: nothing is saved yet),
          shadow-2, amber "waiting on you" tag, the exact payload quoted
          on a surface ground, verb+object confirm. Three-region dialog
          shape per repo responsive conventions. */}
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 rounded-[14px] border-input p-0 shadow-2">
          <DialogHeader className="border-b border-border px-4 py-3 pr-12 sm:px-6">
            <span className="w-fit rounded-full border border-attention-line bg-attention-soft px-[9px] py-[3px] text-[12px] font-bold text-attention-text">
              {t('today:needs_you.label')}
            </span>
            <DialogTitle>{t('detail.modal.title')}</DialogTitle>
            <DialogDescription>
              {t('detail.modal.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <textarea
              id="sms-textarea"
              readOnly
              value={smsText}
              rows={14}
              className="w-full rounded-[10px] bg-muted p-3 font-mono text-xs leading-relaxed text-foreground outline-none"
            />
          </div>
          <DialogFooter className="border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
            <Button type="button" size="pill" onClick={handleCopy}>
              {copied ? (
                <>
                  <Check className="size-4" />
                  {t('detail.modal.copied')}
                </>
              ) : (
                <>
                  <Copy className="size-4" />
                  {t('detail.modal.copy')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Row({
  label,
  value,
}: {
  label: string
  value: number | null
}) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="font-mono tabular-nums">
        {value !== null ? formatMoney(value) : '—'}
      </span>
    </div>
  )
}
